import { useCallback, useRef, useState } from "react";

import {
  putProfilePublish,
  type ProfilePublishResponse,
} from "../lib/profile-publish-api";

export interface UseProfilePublishOptions {
  getToken: () => Promise<string | null>;
  // Awaited by mutate() so callers (e.g. the publish confirm modal) can wait
  // for a post-publish snapshot refresh to land before closing — avoids the
  // card flickering old→new state on reveal.
  onSuccess?: (data: ProfilePublishResponse) => void | Promise<void>;
}

export function useProfilePublish({
  getToken,
  onSuccess,
}: UseProfilePublishOptions) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const inFlightRef = useRef(false);

  const mutate = useCallback(
    async (published: boolean) => {
      if (inFlightRef.current) {
        return null;
      }
      inFlightRef.current = true;
      setIsPending(true);
      setError(null);
      try {
        const data = await putProfilePublish(getToken, published);
        await onSuccess?.(data);
        return data;
      } catch (err) {
        const normalized = err instanceof Error ? err : new Error(String(err));
        setError(normalized);
        throw normalized;
      } finally {
        inFlightRef.current = false;
        setIsPending(false);
      }
    },
    [getToken, onSuccess],
  );

  return { error, isPending, mutate };
}
